/**
 * SmartLink Official Verification Slip Templates & Pricing Configuration
 * Defines available slip types, pricing, visual previews, dimensions, and features
 * matching leading Nigerian identity and verification agent platforms.
 */

import { SlipFormatType, VerificationType } from "../types/verification";

export interface SlipOptionConfig {
  id: SlipFormatType;
  name: string;
  badge: string;
  badgeColor: string;
  price: number;
  description: string;
  dimensions: string;
  recommendedFor: string;
  themeColor: string;
  bgGradient: string;
  features: string[];
  isPopular?: boolean;
  isOfficialDefault?: boolean;
  sampleLayout: "STANDARD_SLIP" | "PREMIUM_CARD" | "DIGITAL_GREEN" | "THERMAL_RECEIPT" | "TEXT_DATA";
}

export interface ServiceSlipManifest {
  service: VerificationType;
  title: string;
  portal: string;
  searchMethods: {
    id: string;
    label: string;
    placeholder: string;
    helpText: string;
    maxLength?: number;
    inputType: "text" | "number" | "tel";
  }[];
  slipOptions: SlipOptionConfig[];
}

export const NIN_SLIP_OPTIONS: SlipOptionConfig[] = [
  {
    id: "NIN_REGULAR",
    name: "Regular Slip",
    badge: "Basic Slip",
    badgeColor: "bg-[#F5F7FA] text-[#0F2D5C] border border-[#E5E7EB]",
    price: 180,
    description: "Standard NIN Slip format with demographic fields, barcode, and QR verification.",
    dimensions: "A4 / Letter (Portrait)",
    recommendedFor: "General Identity Verification & Enrolment Confirmation",
    themeColor: "#10b981",
    bgGradient: "from-[#0F2D5C]/10 via-[#0F2D5C]/5 to-[#111827]/10",
    features: [
      "Demographic Profile & Address",
      "Barcode & Scannable QR Code",
      "Printable High-Resolution Layout",
    ],
    isOfficialDefault: true,
    sampleLayout: "STANDARD_SLIP",
  },
  {
    id: "NIN_PREMIUM_WHITE",
    name: "Premium Card",
    badge: "Premium Card",
    badgeColor: "bg-[#F5F7FA] text-[#0F2D5C] border border-[#E5E7EB]",
    price: 250,
    description: "Standard plastic wallet identity card layout with dual-sided print formatting.",
    dimensions: "CR80 Plastic Card Size",
    recommendedFor: "Plastic Card Printing & Wallet ID",
    themeColor: "#0F2D5C",
    bgGradient: "from-[#0F2D5C]/10 via-[#0F2D5C]/5 to-[#111827]/10",
    features: [
      "Front & Back ID Layout",
      "Biometric Photograph",
      "Official Tracking Number & QR Code",
    ],
    isPopular: true,
    sampleLayout: "PREMIUM_CARD",
  },
];

export const BVN_SLIP_OPTIONS: SlipOptionConfig[] = [
  {
    id: "BVN_CARD" as any,
    name: "BVN Card",
    badge: "Plastic Card",
    badgeColor: "bg-[#F5F7FA] text-[#0F2D5C] border border-[#E5E7EB]",
    price: 250,
    description: "Dual-sided landscape wallet card format with customer photograph and verification seal.",
    dimensions: "CR80 (Standard ID Card)",
    recommendedFor: "Wallet Card Printing & Plastic Card Issuance",
    themeColor: "#4338ca",
    bgGradient: "from-[#0F2D5C]/10 via-[#0F2D5C]/5 to-[#111827]/10",
    features: ["Front & Back Card", "Biometric Photo", "QR Code"],
    isPopular: true,
    sampleLayout: "PREMIUM_CARD",
  },
  {
    id: "BVN_SLIP_1" as any,
    name: "BVN Slip",
    badge: "Digital Slip",
    badgeColor: "bg-[#F5F7FA] text-[#0F2D5C] border border-[#E5E7EB]",
    price: 200,
    description: "Digital verification slip with banking demographics, photo, and seal.",
    dimensions: "A4 / Letter (Portrait)",
    recommendedFor: "Banks & General KYC",
    themeColor: "#1d4ed8",
    bgGradient: "from-[#0F2D5C]/10 via-[#0F2D5C]/5 to-[#111827]/10",
    features: ["Standard Verification Seal", "Verified Photo & Phone", "QR Code"],
    isOfficialDefault: true,
    sampleLayout: "STANDARD_SLIP",
  },
];

export const SERVICE_MANIFESTS: Record<string, ServiceSlipManifest> = {
  NIN: {
    service: "NIN",
    title: "National Identification Number (NIN)",
    portal: "NIN Validation Portal",
    searchMethods: [
      {
        id: "BY_NIN",
        label: "Search by 11-Digit NIN",
        placeholder: "e.g. 12345678901",
        helpText: "Enter the 11 numeric digits printed on the NIN slip or National e-ID card.",
        maxLength: 11,
        inputType: "number",
      },
      {
        id: "BY_PHONE",
        label: "Search by Linked Phone Number",
        placeholder: "e.g. 08012345678",
        helpText: "Enter the registered phone number linked to the customer's NIN record.",
        maxLength: 11,
        inputType: "tel",
      },
      {
        id: "BY_TRACKING_ID",
        label: "Search by Tracking ID",
        placeholder: "e.g. TRACK-892182049",
        helpText: "Enter the alphanumeric tracking code issued upon biometric enrolment.",
        inputType: "text",
      },
      {
        id: "BY_VNIN",
        label: "Search by Virtual NIN (vNIN)",
        placeholder: "e.g. 1234567890123456",
        helpText: "Enter the 16-character Virtual NIN generated via the Mobile ID app.",
        maxLength: 16,
        inputType: "text",
      },
      {
        id: "BY_DEMOGRAPHICS",
        label: "Search by Demographics (Name, DOB & State)",
        placeholder: "First Name & Surname",
        helpText: "Match records using full legal name, date of birth, and state of origin.",
        inputType: "text",
      },
    ],
    slipOptions: NIN_SLIP_OPTIONS,
  },
  BVN: {
    service: "BVN",
    title: "Bank Verification Number (BVN)",
    portal: "BVN Validation Portal",
    searchMethods: [
      {
        id: "BY_BVN",
        label: "Search by 11-Digit BVN",
        placeholder: "e.g. 22233344455",
        helpText: "Enter the 11-digit Bank Verification Number issued by your bank.",
        maxLength: 11,
        inputType: "number",
      },
      {
        id: "BY_PHONE",
        label: "Search by Banking Phone Number",
        placeholder: "e.g. 08098765432",
        helpText: "Enter the phone number registered with the customer's bank account.",
        maxLength: 11,
        inputType: "tel",
      },
    ],
    slipOptions: BVN_SLIP_OPTIONS,
  },
};

export function getSlipOptionById(formatType: string): SlipOptionConfig {
  const all = [...NIN_SLIP_OPTIONS, ...BVN_SLIP_OPTIONS];
  const found = all.find((s) => s.id === formatType);
  return (
    found || {
      id: "NIN_REGULAR",
      name: "Regular Slip",
      badge: "Basic Slip",
      badgeColor: "bg-[#F5F7FA] text-[#0F2D5C] border border-[#E5E7EB]",
      price: 180,
      description: "Standard NIN verification slip with barcode & QR code.",
      dimensions: "A4 / Letter (Portrait)",
      recommendedFor: "General KYC & Identification",
      themeColor: "#10b981",
      bgGradient: "from-[#0F2D5C]/10 to-[#111827]/10",
      features: ["Verification Seals", "QR Code Verification", "Demographic Details"],
      sampleLayout: "STANDARD_SLIP",
    }
  );
}

/**
 * Ensures photo URLs, raw Base64 strings, or data URIs render correctly
 * without broken image tags across all slip templates and UI cards.
 */
export function normalizePhotoUrl(raw: unknown): string {
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "false" ||
    trimmed === "{}" ||
    trimmed === "[]"
  ) {
    return "";
  }

  if (
    trimmed.startsWith("data:image/") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("blob:") ||
    trimmed.startsWith("/api/storage/") ||
    trimmed.startsWith("/")
  ) {
    return trimmed;
  }

  const cleanBase64 = trimmed.replace(/\s+/g, "");
  if (cleanBase64.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${cleanBase64}`;
  }
  if (cleanBase64.startsWith("iVBORw0KGgo")) {
    return `data:image/png;base64,${cleanBase64}`;
  }
  if (cleanBase64.startsWith("R0lGOD")) {
    return `data:image/gif;base64,${cleanBase64}`;
  }
  if (cleanBase64.startsWith("UklGR")) {
    return `data:image/webp;base64,${cleanBase64}`;
  }
  if (cleanBase64.startsWith("PHN2Zy") || cleanBase64.startsWith("PD94bWw")) {
    return `data:image/svg+xml;base64,${cleanBase64}`;
  }

  if (cleanBase64.length > 30 && /^[A-Za-z0-9+/=]+$/.test(cleanBase64)) {
    return `data:image/jpeg;base64,${cleanBase64}`;
  }

  return trimmed;
}
